import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { UncontrolledPopover } from 'reactstrap'
import PropTypes from 'prop-types'

import SeriesRow from './SeriesRow'
import Placeholder from '../common/Placeholder'
import Loader from '../common/loader/legacy-loaders/Op36Loader-web'
import Pagination from '../common/Pagination'
import SeriesEventRow from './SeriesEventRow'
import colors from '../../assets/styles/globals.scss'

import styles from './SeriesList.module.scss'
import { ReactComponent as GolferIcon } from '../../assets/images/common/icons/golfer-half-icon.svg'
import classNames from 'classnames'
import { useNavigate } from 'react-router-dom'
import CustomPlaceholder from '../common/CustomPlaceholder'

const SeriesList = ({
  series,
  typeCompleted,
  loader,
  searchTerm,
  activePage,
  setActivePage,
  handleSeriesMenuClick,
  removeSeries,
  navigateToEdit,
  navigateToEmails,
  localScores,
}) => {
  const { t } = useTranslation()
  const pageCount = useSelector(
    state => typeCompleted ? state.series?.completedSeriesPagination?.totalPages : state.series?.upcomingSeriesPagination?.totalPages
  )
  const [expandedSeriesId, setExpandedSeriesId] = useState(-1)
  const navigate = useNavigate()

  const handleCreateEvent = () => {
    navigate('/series/new')
  }

  useEffect(() => {
    if (!series?.length > 0) {
      return
    }

    setExpandedSeriesId(series[0].id)
  }, [series])

  if (loader) {
    return (
      <div className='col-md-6 mx-auto'>
        <Loader message={t('events.series.loadingSeries')} />
      </div>
    )
  }

  if (!series?.length) {
    if (searchTerm) {
      return (
        <div className='mt-5 w-100'>
          <Placeholder
            title={t('events.series.noResultsTitle')}
            icon={'fa-search'}
            subtext1={t('events.series.noResultsSubtext1')}
            subtext2={t('events.series.noResultsSubtext2')}
          />
        </div>
      )
    } else {

      if (typeCompleted) {
        return (
          <CustomPlaceholder
            icon = {<GolferIcon id='icon' fill={colors.lightNavy} width={64} height={64}/>}
            subtext = {t('events.series.completedPlaceholder')}
            title={t('events.series.noCompletedTitle')}
          />
        )
      }

      return (
        <CustomPlaceholder
          icon = {<GolferIcon id='icon' fill={colors.lightNavy} width={64} height={64}/>}
          subtext = {t('events.nineHoleEventsPlaceholder')}
          title={t('events.series.noUpcomingTitle')}
          handleAction = {handleCreateEvent}
          guideText = {t('events.series.setupGuideText')}
          buttonText = {t('events.common.createEventSeries')}
          guideLink={'https://intercom.help/op-36/en/articles/11145257-how-do-i-set-up-a-9-hole-event-series'} />
      )
    }
  }

  return (
    <>
      {pageCount > 1 && (
        <Pagination
          pageCount={pageCount}
          activePage={activePage}
          fetchData={(newPage) => setActivePage(newPage)}
        />
      )}
      <div className={pageCount > 1 ? styles.seriesListPaginated : styles.seriesList}>
        {series.map((series, index) => {
          const isExpanded = series.id === expandedSeriesId
          return (
            <div className={classNames('row mx-0', styles.seriesGroup, isExpanded && styles.expanded)} key={series.id}>
              <SeriesRow
                series={series}
                isFirstChild={index === 0}
                handleSeriesMenuClick={handleSeriesMenuClick}
              />
              {series.events.map((e, index) =>
                <SeriesEventRow
                  key={e.id}
                  event={{ ...e, index: index }}
                  inProgress={localScores?.[e.id]?.['scores']}
                />)
              }
              <UncontrolledPopover
                placement='bottom'
                target='seriesMenuButton'
                trigger='legacy'
              >
                <div className={classNames('d-flex flex-column', styles.seriesActions)}>
                  <p aria-hidden='true' onClick={() => navigateToEdit(1)}>{t('events.common.editEventSeries')}</p>
                  <p aria-hidden='true' onClick={() => navigateToEdit(2)}>{t('events.series.editSchedule')}</p>
                  <p aria-hidden='true' onClick={() => navigateToEdit(3)}>{t('events.series.editMasterRoster')}</p>
                  <p aria-hidden='true' onClick={() => navigateToEmails()}>{t('events.common.sendAnEmail')}</p>
                  <p aria-hidden='true' onClick={() => removeSeries()}>{t('events.deleteEventSeries')}</p>
                </div>
              </UncontrolledPopover>
            </div>
          )
        }
        )}
      </div>
    </>
  )
}

SeriesList.propTypes = {
  typeCompleted: PropTypes.bool,
  series: PropTypes.array,
  loader: PropTypes.bool,
  searchTerm: PropTypes.string,
  activePage: PropTypes.number,
  setActivePage: PropTypes.func,
  handleSeriesMenuClick: PropTypes.func,
  navigateToEdit: PropTypes.func,
  removeSeries: PropTypes.func,
  navigateToEmails: PropTypes.func,
  localScores: PropTypes.object,
}

export default SeriesList
